/** TanStack Query keys to use in the app.
 * This is the query key that will be used to process the request.
 */
export const QUERY_KEYS = {
	TOKEN_PAST_12H: "tokenPast12h",
	MODEL_BALANCE: "modelBalance",
	BROADCAST_STATUS_DATA: "broadcastStatusData",
	PRIVATE_REQUEST_DATA: "privateRequestData",
	BLACK_LIST_DATA: "blackListData",
	NEWS_DATA: "newsData",
	PAYMENT_METHODS_STATE: "paymentMethodsState",
	POST_LIST: "postList",
	VERIFICATION_INFO: "verificationInfo",
	BIO: "bio",
	EMOJI_LIST: "emojiList",
	ALL_CATEGORIES_USERS: "allCategoriesUsers",
	BANNER_CONFIG: "bannerConfig",
	COUNTRIES: "countries",
	LANGUAGES: "languages",
	ALL_AVAILABLE_TAGS: "allAvailableTags",
	MODEL_TAG_LIST: "modelTagList",
	PERMISSION: "permission",
	GET_VERIFICATION_TEXT: "verificationText",
	GET_BROADCAST_SETTINGS: "broadcastSettings",
	VR_VIDEO_HISTORY: "vrVideoHistory",
	REMARK_LIST: "remarkList",
	TARIFF_DATA: "tariffData",
	TIP_MENU_STATUS: "tipMenuStatus",
	TIP_MENU_DATA: "tipMenuData",
	TOKEN_INFO_HISTORY: "tokenInfoHistory",
	TOKENS_IN_FILE: "tokensInFile",
	CUSTOM_STREAM_LINKS: "customStreamLinks",
	MUTED_USERS_LIST: "mutedUsersList",
	BLOCKED_COUNTRY_LIST: "blockedCountryList",
	MODEL_EMAIL: "modelEmail",
	PAYMENT_METHODS_DATA: "paymentMethodsData",
	SINGLE_APPLICATION: "getSingleApplication",
	GET_GOAL_LIST: "getGoalList",
	BIG_WISH_CONTENT: "bigWishContent",
	INTEGRATION_CONTENT: "integrationContent",
	BIG_WISH_STATUS: "bigWishStatus",
	DEFAULT_TIP_MENU_ITEMS: "defaultTipMenuItems",
	TOYS_SESSION_TOKEN: "toysSessionToken",
	TOYS_SESSION: "toysSession",
	TOYS_CAPABILITIES: "toysCapabilities",
	TOYS_HOW_TO_GUIDE: "toysHowToGuide",
} as const;

/** API prefixes to display an error in the console */
export const API_PREFIXES = {
	GET_TOKEN_PAST_12H: "tokens/getTokenPast12h",
	GET_TOKEN_INFO_HISTORY: "tokens/getTokensInfoHistory",
	GET_TOKENS_IN_FILE: "tokens/getTokensInFile",
	GET_PAYMENT_METHODS_STATE: "payoutSettings/getPaymentMethodsState",
	GET_POST_LIST: "modelCamPosts/getPosts",
	CREATE_POST: "modelCamPosts/createPost",
	UPDATE_POST: "modelCamPosts/updatePost",
	DELETE_POST: "modelCamPosts/deletePost",
	GET_MODEL_BALANCE: "tariff/getModelBalance",
	GET_NEWS_LIST: "news/getNewsList",
	GET_MODEL_BIO: "models/getModelBio",
	UPDATE_MODEL_BIO: "models/updateModelBio",
	USER_SIGN_IN: "authentication/userSignIn",
	USER_SIGN_OUT: "authentication/userSignOut",
	GET_VERIFICATION_INFO: "verification/getVerificationInfo",
	START_VERIFICATION: "verification/startVerification",
	GET_VERIFICATION_TEXT: "bucket/text_age_verification.json",
	GET_BROADCAST_STATUS_DATA: "broadcast/broadcastStatus",
	GET_PRIVATE_REQUEST_LIST: "privateRequests/getPrivateRequest",
	GET_BLACK_LIST: "blacklist/getBlackList",
	START_BROADCAST: "broadcast/startBroadcast",
	STOP_BROADCAST: "broadcast/stopBroadcast",
	START_AWAY_MODE: "broadcast/startAwayMode",
	STOP_AWAY_MODE: "broadcast/stopAwayMode",
	START_SPY_MODE: "outerPrivate/startSpyMode",
	STOP_SPY_MODE: "outerPrivate/stopSpyMode",
	PRIVATE_REQUEST_ACCEPT: "privateRequests/privateRequestAccept",
	PRIVATE_REQUEST_DECLINE: "privateRequests/privateRequestDecline",
	BLOCK_USER_BY_ID: "black_list/blockUserById",
	UNBLOCK_USER_BY_ID: "black_list/unblockUserById",
	MUTE_USER_BY_ID: "chat/muteUserById",
	UNMUTE_USER_BY_ID: "chat/unMuteUserById",
	GET_EMOJI_LIST: "messenger/getAllEmojisByModel",
	GET_ALL_CATEGORIES_USERS: "messenger/getAllCategoriesUsers",
	GET_BANNER_CONFIG: "banner-config.json",
	GET_COUNTRY_LIST: "models/getCountryList",
	GET_LANGUAGE_LIST: "models/getLanguageList",
	GET_TAG_LIST_ALL: "tags/getAllAvailableTags",
	GET_TAG_LIST_MY: "tags/getModelTagList",
	SAVE_MODEL_TAG_LIST: "tags/saveModelTagList",
	GET_PERMISSION: "modelContentPermission/getPermissionValue",
	UPDATE_PERMISSION: "modelContentPermission/updatePermissionValue",
	UPDATE_PHOTO: "photos/updatePhoto",
	GET_BROADCAST_SETTINGS: "broadcast/getBroadcastSettings",
	GET_VR_VIDEO_HISTORY: "gallery/getVrVideoHistory",
	SEND_MESSAGE: "chat/sendMessage",
	GET_REMARK_LIST: "remarks/getRemarkList",
	CREATE_REMARK: "remarks/createRemark",
	UPDATE_REMARK: "remarks/updateRemark",
	DELETE_REMARK: "remarks/deleteRemark",
	GET_TARIFF_DATA: "tariff/getTariffData",
	UPDATE_TARIFF_DATA: "tariff/updateTariffData",
	GET_TIP_MENU_STATUS: "tipMenu/getTipMenuStatus",
	TOGGLE_TIP_MENU_STATUS: "tipMenu/toggleTipMenuStatus",
	GET_TIP_MENU_DATA: "tipMenu/getTipMenuData",
	UPDATE_TIP_MENU_DATA: "tipMenu/updateTipMenuData",
	DELETE_TIP_MENU_ITEM: "tipMenu/deleteTipMenuItem",
	UPDATE_BROADCAST_TEXT_STATUS: "broadcast/updateBroadcastTextStatus",
	GET_CUSTOM_STREAM_LINKS: "custom-stream/getCustomStreamLinks",
	ADD_CUSTOM_STREAM_LINK: "custom-stream/addCustomStreamLink",
	EDIT_CUSTOM_STREAM_LINK: "custom-stream/editCustomStreamLink",
	DELETE_CUSTOM_STREAM_LINK: "custom-stream/deleteCustomStreamLink",
	GET_MUTED_USERS_LIST: "chat/getMutedUsersList",
	CHANGE_PASSWORD: "authentication/changePassword",
	RESTORE_PASSWORD: "authentication/restorePassword",
	RESET_PASSWORD: "authentication/resetPassword",
	GET_BLOCKED_COUNTRY_LIST: "privacy/getBlockedCountryList",
	UPDATE_BLOCKED_COUNTRY_LIST: "privacy/updateBlockedCountryList",
	GET_MODEL_EMAIL: "privacy/getModelEmail",
	UPDATE_MODEL_EMAIL: "privacy/updateModelEmail",
	GET_PAYMENT_METHODS_DATA: "payoutSettings/getPaymentMethodsData",
	CREATE_PAYMENT_METHOD: "payoutSettings/createPaymentMethod",
	TOGGLE_PAYMENT_METHOD: "payoutSettings/togglePaymentMethod",
	CREATE_INDIVIDUAL_MODEL: "modelIndividual/createIndividualModel",
	CREATE_SIGN_UP: "registration/createSignUp",
	GET_SINGLE_APPLICATION: "registration/getSingleApplication",
	UPDATE_SINGLE_APPLICATION: "registration/updateSingleApplication",
	VERIFY_SIGN_UP_APPLICATION: "registration/verifySignUpApplication",
	GET_GOAL_LIST: "goals/getGoalList",
	UPDATE_GOAL_QUEUE: "goals/updateGoalQueue",
	DELETE_GOAL_BY_ID: "goals/deleteGoalById",
	START_GOAL: "goals/startGoal",
	GET_BIG_WISH_CONTENT: "bucket/bigWishContent.json",
	GET_INTEGRATION_CONTENT: "bucket/json/integration_content.json",
	GET_BIG_WISH_STATUS: "bigwish/getStatus",
	CREATE_BIG_WISH_CREATOR: "bigwish/createCreator",
	GET_BIG_WISH_LOGIN_LINK: "bigwish/getLoginLink",
	GET_BIG_WISH_PROFILE_LINK: "bigwish/getProfileLink",
	GET_DEFAULT_TIP_MENU_ITEMS: "bucket/default_tip_menu_items.json",
	GET_TOYS_SESSION_TOKEN: "toys/getSessionToken",
	GET_TOYS_SESSION: "toysGate/getToysSession",
	GET_TOYS_CAPABILITIES: "toysGate/getCapabilities",
	UPDATE_TOYS_MOTION: "toysGate/updateMotion",
	ACCEPT_TOYS_CONTROL: "toysGate/accept",
	PLAY_TOYS: "toysGate/play",
	STOP_TOYS: "toysGate/stop",
	DECLINE_TOYS_CONTROL: "toysGate/decline",
	GET_TOYS_HOW_TO_GUIDE: "bucket/json/toys_how_to_guide.json",
} as const;
